import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ErrorService} from '@services/error/error.service';
import {createHttpParams} from '@shared/functions';
import {IGroup, WiFiSettings} from '@interfaces/group';
import {IResponse} from '@interfaces/response';
import {merge, Observable, throwError} from 'rxjs';
import {catchError, filter, mergeMap, share} from 'rxjs/operators';
import {IDevice, IDeviceSystemVolume} from '@interfaces/devices';
import {DeviceService} from '@services/device/device.service';
import {TranslateService} from '@ngx-translate/core';
import {ModalService} from '@services/modal/modal.service';
import {DeviceProofMode, GroupProofResponse} from '@shared/device-proof';

@Injectable({
    providedIn: 'root'
})
export class GroupService {
    constructor(
        private http: HttpClient,
        private errorService: ErrorService,
        private deviceService: DeviceService,
        private translate: TranslateService,
        private modalService: ModalService,
    ) {
    }

    public setProofMode(id: number, mode: DeviceProofMode): Observable<GroupProofResponse> {
        return this.http.post<GroupProofResponse>(`groups/proof-mode/${id}`, {mode}).pipe(
            catchError(error => {
                this.errorService.showError(error, false);
                return throwError(() => error);
            })
        );
    }

    public checkGroupEventsForSafeDeletion(group: IGroup): boolean {
        let canDelete = true;
        if (group.areas && group.areas.length) {
            const events = [];
            group.areas.forEach((event) => {
                if (event && event.playlist && event.schedule) {
                    events.push(event);
                }
            });
            if (events.length) {
                this.translate.get('errors.cantDeleteGroup')
                    .subscribe((res) => this.modalService.showMsg(res));
                canDelete = false;
            }
        }
        return canDelete;
    }

    public getGroups(
        {limit, skip = 0, order = '-group_id', search = '', onSchedule = 0}:
            { limit: number, skip?: number, order?: string, search?: string, onSchedule?: 1 | 0 }
    ): Observable<IResponse<IGroup>> {
        const reqUrl = onSchedule ? 'groups/scheduled' : 'groups';
        const params = createHttpParams({
            skip, limit, search, order
        });

        return this.http.get<IResponse<IGroup>>(`${reqUrl}`, {params})
            .pipe(
                share(),
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    public getDevicesByGroupId({groupId,
                                limit,
                                skip = 0,
                                order = '-id',
                                search = '',
                               }: { groupId: number,
                                    skip: number,
                                    limit: number,
                                    order?: string,
                                    search?: string
                                }): Observable<IResponse<IDevice>> {
        const params = createHttpParams({
            skip, limit, order, search
        });

        return this.http.get<IResponse<IDevice>>(`groups/${groupId}/devices`, {params})
            .pipe(
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    public getGroupById(groupId: number): Observable<IGroup> {
        return this.http.get<IGroup>(`groups/${groupId}`).pipe(
            catchError(err => {
                this.errorService.showError(err);
                return throwError(err);
            })
        );
    }

    public createGroup({name, description = '', timezone, devicesId}:
                           { name: string, description: string, timezone: string | null, devicesId?: number[] | undefined }): Observable<IGroup> {
        const createGroup$ = this.http.post<IGroup>('groups', {name, description, timezone})
            .pipe(
                share(),
                catchError(err => {
                    this.errorService.showError(err, false);
                    return throwError(err);
                })
            );

        const addGroupOnly$ = createGroup$
            .pipe(
                filter(() => !devicesId?.length)
            );

        // TODO (fix) part down bellow need to add devices after creating group,
        // because back can't do that in one request
        const addGroupWithDevices = createGroup$
            .pipe(
                filter(() => !!devicesId?.length),
                mergeMap(group => this.deviceService.addDevicesToGroup(group.id, devicesId as number[])),
            );

        return merge(addGroupOnly$, addGroupWithDevices) as Observable<IGroup>;
    }

    public updateGroup(props: Partial<IGroup>, groupId: number): Observable<IGroup> {
        return this.http.patch<IGroup>(`groups/${groupId}`, {
                ...props
            }
        )
            .pipe(
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    public updateGroupOS(groupid: number): Observable<any> {
        return this.http.post(`groups/firmware/update/${groupid}`, {})
            .pipe(
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    public remove(id: number): Observable<any> {
        return this.http.delete(`groups/${id}`)
            .pipe(
                share(),
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    public updateGroupVolumeSystem(props: Partial<IDeviceSystemVolume>, groupId: number): Observable<IDeviceSystemVolume> {
        return this.http.patch<IDeviceSystemVolume>(`groups/system-volume/${groupId}`, {...props})
            .pipe(
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    public updateGroupWiFiSettings(groupId: number, props: Partial<WiFiSettings>): Observable<WiFiSettings> {
        return this.http.patch<WiFiSettings>(`groups/wifi-settings/${groupId}`, {...props})
            .pipe(
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    public resendGroupSchedule(id: number): Observable<{ message: string }> {
        const stream$ = this.http.post<{ message: string }>(`groups/${id}/resend-schedule`, {}).pipe(
            catchError(err => {
                this.errorService.showError(err, false);
                return throwError(err);
            }),
            share(),
        );

        stream$.subscribe((response) => {
            this.modalService.showMsg(response.message, null, {closePreviousModal: false});
        });

        return stream$;
    }

    public restartDevices(id: number): Observable<any> {
        return this.http.post<{ message: string }>(`groups/restart/${id}`, {})
            .pipe(
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    public groupScreenLockController(id: number, status: boolean): Observable<any> {
        return this.http
            .post<Observable<any>>(`groups/${status ? 'screenlock' : 'screenunlock'}/${id}`, {})
            .pipe(
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    groupDownloadManagerController(group_id: number, checked: boolean): Observable<any> {
        return this.http
            .post<Observable<any>>(`groups/download-manager/${group_id}`, {
                enabled: checked
            })
            .pipe(
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }
}
